// components/modals/ReuploadModal.js
import React, { useState } from "react";
import { jobsAPI } from "../../../services/api";
import { formatFileSize, isExcelFile } from "../../utils/jobUtils";
import { readWB, parseAR04, compareAR04WithMaster } from "../../excelUtils";

const ReuploadModal = ({ show, job, onClose, onSuccess }) => {
  const [newAR04File, setNewAR04File] = useState(null);
  const [reuploadProgress, setReuploadProgress] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  if (!show) return null;

  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate file type
    if (!isExcelFile(file)) {
      alert("กรุณาเลือกไฟล์ Excel (.xlsx หรือ .xls) เท่านั้น");
      return;
    }

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("ไฟล์มีขนาดใหญ่เกินไป (สูงสุด 50MB)");
      return;
    }

    setNewAR04File(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const uploadNewAR04ForJob = async () => {
    try {
      if (!newAR04File) {
        alert("กรุณาเลือกไฟล์ AR04 ใหม่");
        return;
      }

      setReuploadProgress(0);

      console.log(`🔄 Processing new AR04 for job ${job.id}...`);

      // Parse new AR04 file
      const wbAR04 = await readWB(newAR04File);
      const newAR04Data = parseAR04(wbAR04);
      console.log(`📊 Parsed ${newAR04Data.length} new AR04 records`);
      setReuploadProgress(25);

      // Get original master data for this job
      const masterResponse = await jobsAPI.getMasterData(job.id);
      if (!masterResponse.data.success || !masterResponse.data.data) {
        alert("ไม่พบข้อมูล MASTER FILE สำหรับงานนี้");
        return;
      }

      const masterData = masterResponse.data.data;
      console.log(
        `📊 Using ${masterData.length} master records for comparison`
      );
      setReuploadProgress(50);

      // Compare new AR04 with original master data
      const comparisonResult = compareAR04WithMaster(newAR04Data, masterData);
      console.log(
        `✅ Generated ${comparisonResult.data.length} comparison results`
      );
      setReuploadProgress(75);

      // Store new comparison results
      await jobsAPI.storeComparisonResults(job.id, comparisonResult.data);
      console.log(`✅ Stored new comparison results for job ${job.id}`);

      // Update job status based on errors
      const errorCount = comparisonResult.data.filter(
        (record) =>
          record.Remark &&
          record.Remark !== "ตรงกันทุกประการ" &&
          record.Remark.trim() !== ""
      ).length;

      await jobsAPI.update(job.id, {
        progress_percentage: 100,
        status: errorCount === 0 ? "FIXED" : "COMPLETED",
      });

      // Log history entry for AR04 reupload with previous error data
      await logReuploadHistory(job, errorCount, comparisonResult.data.length);

      setReuploadProgress(100);

      setTimeout(() => {
        if (errorCount === 0) {
          alert(
            `🎉 สำเร็จ! ไฟล์ AR04 ใหม่ไม่มีข้อผิดพลาด\n\n` +
              `✅ งานได้รับการแก้ไขเรียบร้อยแล้ว\n` +
              `📊 ตรวจสอบ ${comparisonResult.data.length} รายการ\n` +
              `🔄 สถานะงานเปลี่ยนเป็น "แก้ไขเรียบร้อย"`
          );
        } else {
          alert(
            `⚠️ ไฟล์ AR04 ใหม่ยังคงมีข้อผิดพลาด\n\n` +
              `❌ พบข้อผิดพลาด ${errorCount} รายการ\n` +
              `📊 จากทั้งหมด ${comparisonResult.data.length} รายการ\n` +
              `💡 กรุณาตรวจสอบและอัปโหลดไฟล์ใหม่อีกครั้ง`
          );
        }

        onSuccess();
      }, 1000);
    } catch (error) {
      console.error("New AR04 upload failed:", error);
      alert("เกิดข้อผิดพลาดในการอัปโหลด AR04 ใหม่: " + error.message);
      setReuploadProgress(null);
    }
  };

  const logReuploadHistory = async (job, errorCount, totalRecords) => {
    try {
      // Get the previous error count before updating
      const previousErrorCount = job.error_count;

      // Get current error data before replacement
      let previousErrors = [];
      try {
        const currentResultsResponse = await jobsAPI.getComparisonResults(
          job.id
        );
        if (
          currentResultsResponse.data.success &&
          currentResultsResponse.data.data
        ) {
          previousErrors = currentResultsResponse.data.data.filter(
            (record) =>
              record.Remark &&
              record.Remark !== "ตรงกันทุกประการ" &&
              record.Remark.trim() !== ""
          );
          console.log(
            `📊 Captured ${previousErrors.length} previous error records before reupload`
          );
        }
      } catch (previousDataError) {
        console.warn("Could not get previous error data:", previousDataError);
      }

      // Always include previous_error_data, even if previousErrors is empty
      const historyEntry = {
        action_type: "REUPLOAD",
        description:
          errorCount === 0
            ? "อัปโหลด AR04 ใหม่ - แก้ไขข้อผิดพลาดสำเร็จ"
            : `อัปโหลด AR04 ใหม่ - ยังคงมีข้อผิดพลาด ${errorCount} รายการ`,
        error_count: errorCount,
        total_records: totalRecords,
        previous_error_count: previousErrorCount,
        details: `อัปโหลดไฟล์ AR04 ใหม่สำหรับงาน "${
          job.job_name
        }" เมื่อ ${new Date().toLocaleString(
          "th-TH"
        )} - ปรับปรุงจาก ${previousErrorCount} เป็น ${errorCount} ข้อผิดพลาด`,
        files_involved: { ar04_file: newAR04File.name },
        previous_error_data: previousErrors, // always include, even if empty
      };

      // Try to store with historical error data
      try {
        await jobsAPI.addJobHistoryWithErrorData(job.id, historyEntry);
        console.log(
          `📝 Successfully logged AR04 reupload history with ${previousErrors.length} previous error records`
        );
      } catch (historyApiError) {
        console.warn("Enhanced history API not available, using basic history");
        // Fallback to basic history
        await jobsAPI.addJobHistory(job.id, {
          action_type: historyEntry.action_type,
          description: historyEntry.description,
          error_count: historyEntry.error_count,
          total_records: historyEntry.total_records,
          previous_error_count: historyEntry.previous_error_count,
          details: historyEntry.details,
          files_involved: historyEntry.files_involved,
          previous_error_data: previousErrors, // always include
        });
        console.log(`📝 Logged basic AR04 reupload history for job ${job.id}`);
      }
    } catch (historyError) {
      console.warn("Failed to log AR04 reupload history:", historyError);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: "600px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 style={{ fontSize: "20px", fontWeight: "600" }}>
            📤 อัปโหลด AR04 ใหม่
          </h2>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {/* Job Info */}
          <JobInfoSection job={job} />

          {/* Instructions */}
          <InstructionsAlert />

          {/* File Upload Area */}
          <FileUploadArea
            file={newAR04File}
            onFileSelect={handleFileSelect}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            dragOver={dragOver}
            onRemoveFile={() => setNewAR04File(null)}
          />

          {/* Upload Progress */}
          {reuploadProgress !== null && (
            <UploadProgress progress={reuploadProgress} />
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-outline"
            onClick={onClose}
            disabled={reuploadProgress !== null}
          >
            ยกเลิก
          </button>
          <button
            className="btn btn-success"
            onClick={uploadNewAR04ForJob}
            disabled={!newAR04File || reuploadProgress !== null}
          >
            {reuploadProgress !== null
              ? "กำลังประมวลผล..."
              : "🔄 เปรียบเทียบใหม่"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Sub-components
const JobInfoSection = ({ job }) => (
  <div style={{ textAlign: "center", marginBottom: "20px" }}>
    <h3 style={{ color: "#dc2626", marginBottom: "8px" }}>
      งาน: {job.job_name}
    </h3>
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: "6px",
        padding: "8px 12px",
      }}
    >
      <span style={{ fontSize: "16px" }}>⚠️</span>
      <span style={{ color: "#dc2626", fontWeight: "600" }}>
        ข้อผิดพลาด: {job.error_count} รายการ
      </span>
    </div>
  </div>
);

const InstructionsAlert = () => (
  <div className="alert alert-info">
    <span>ℹ️</span>
    <div>
      <strong>วิธีการทำงาน:</strong>
      <ul style={{ margin: "8px 0 0 20px", fontSize: "14px" }}>
        <li>อัปโหลดไฟล์ AR04 ใหม่ที่แก้ไขแล้ว</li>
        <li>ระบบจะเปรียบเทียบ AR04 ใหม่กับข้อมูล Tax เดิม</li>
        <li>หากไม่มีข้อผิดพลาด สถานะจะเปลี่ยนเป็น "แก้ไขเรียบร้อย"</li>
        <li>หากมีข้อผิดพลาดอยู่ สามารถอัปโหลดใหม่ได้อีก</li>
      </ul>
    </div>
  </div>
);

const FileUploadArea = ({
  file,
  onFileSelect,
  onDrop,
  onDragOver,
  onDragLeave,
  dragOver,
  onRemoveFile,
}) => (
  <div className="form-group">
    <label className="form-label">เลือกไฟล์ AR04 ใหม่ *</label>
    <div
      className={`upload-area ${dragOver ? "drag-over" : ""}`}
      style={{
        padding: "30px",
        minHeight: "150px",
        border: dragOver ? "2px dashed #3b82f6" : "2px dashed #d1d5db",
        backgroundColor: dragOver ? "#eff6ff" : "#f9fafb",
        borderRadius: "8px",
        textAlign: "center",
        transition: "all 0.2s ease",
        cursor: "pointer",
      }}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      {file ? (
        <SelectedFileDisplay
          file={file}
          onRemove={onRemoveFile}
          onFileSelect={onFileSelect}
        />
      ) : (
        <EmptyUploadArea onFileSelect={onFileSelect} dragOver={dragOver} />
      )}
    </div>
  </div>
);

const SelectedFileDisplay = ({ file, onRemove, onFileSelect }) => (
  <div>
    <div
      className="upload-icon"
      style={{ fontSize: "48px", marginBottom: "16px" }}
    >
      ✅
    </div>
    <div
      className="upload-text"
      style={{ fontSize: "16px", fontWeight: "600" }}
    >
      {file.name}
    </div>
    <div
      style={{
        fontSize: "14px",
        color: "#64748b",
        marginTop: "8px",
        marginBottom: "16px",
      }}
    >
      {formatFileSize(file.size)}
    </div>
    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
      <button
        className="btn btn-outline btn-sm"
        onClick={onRemove}
        type="button"
      >
        🗑️ ลบไฟล์
      </button>
      <label htmlFor="new-ar04-file-input" className="btn btn-outline btn-sm">
        📁 เลือกไฟล์ใหม่
      </label>
      <input
        type="file"
        accept=".xls,.xlsx"
        onChange={(e) => onFileSelect(e.target.files[0])}
        style={{ display: "none" }}
        id="new-ar04-file-input"
      />
    </div>
  </div>
);

const EmptyUploadArea = ({ onFileSelect, dragOver }) => (
  <div>
    <div
      className="upload-icon"
      style={{
        fontSize: "48px",
        marginBottom: "16px",
        color: dragOver ? "#3b82f6" : "#6b7280",
      }}
    >
      📄
    </div>
    <div
      className="upload-text"
      style={{
        fontSize: "18px",
        fontWeight: "600",
        marginBottom: "8px",
        color: dragOver ? "#3b82f6" : "#374151",
      }}
    >
      {dragOver ? "วางไฟล์ที่นี่" : "AR04.xlsx (แก้ไขใหม่)"}
    </div>
    <div
      className="upload-subtext"
      style={{
        fontSize: "14px",
        color: "#6b7280",
        marginBottom: "16px",
      }}
    >
      {dragOver
        ? "วางไฟล์ Excel เพื่ออัปโหลด"
        : "ลากไฟล์มาวาง หรือคลิกเพื่อเลือกไฟล์"}
    </div>
    <input
      type="file"
      accept=".xls,.xlsx"
      onChange={(e) => onFileSelect(e.target.files[0])}
      style={{ display: "none" }}
      id="new-ar04-upload"
    />
    <label
      htmlFor="new-ar04-upload"
      className="btn btn-primary btn-sm"
      style={{ cursor: "pointer" }}
    >
      📁 เลือกไฟล์ AR04
    </label>
  </div>
);

const UploadProgress = ({ progress }) => (
  <div style={{ marginTop: "20px" }}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "8px",
      }}
    >
      <span>กำลังประมวลผล AR04 ใหม่...</span>
      <span>{progress}%</span>
    </div>
    <div
      style={{
        width: "100%",
        height: "8px",
        background: "#e2e8f0",
        borderRadius: "4px",
      }}
    >
      <div
        style={{
          width: `${progress}%`,
          height: "100%",
          background: "#10b981",
          borderRadius: "4px",
          transition: "width 0.3s ease",
        }}
      />
    </div>
    <div
      style={{
        fontSize: "12px",
        color: "#64748b",
        marginTop: "8px",
      }}
    >
      {progress < 30 && "กำลังอ่านไฟล์ AR04 ใหม่..."}
      {progress >= 30 && progress < 60 && "โหลดข้อมูล MASTER FILE..."}
      {progress >= 60 && progress < 90 && "เปรียบเทียบข้อมูล..."}
      {progress >= 90 && progress < 100 && "บันทึกผลการเปรียบเทียบ..."}
      {progress === 100 && "เสร็จสิ้น!"}
    </div>
  </div>
);

export default ReuploadModal;
